import React from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Monitor,
  BookOpen,
  BarChart3,
  Users,
} from "lucide-react";
import LOGO from "../../src/assets/logolmsbts3.png";
const companyLinks = [
  { name: "About company", path: "/about-us" },
  { name: "Contact Support", path: "/contact" },
  { name: "Features", path: "/features" },
];

const productLinks = [
  {
    name: "IVR Guru",
    path: "https://ivrguru.com",
    icon: BookOpen,
  },
  {
    name: "DeetyaSoft",
    path: "https://www.deetyasoft.com",
    icon: BarChart3,
  },
  {
    name: "DND Filter",
    path: "https://dndfilter.com",
    icon: Users,
  },
  {
    name: "Eurofox",
    path: "https://eurofox.in",
    icon: Monitor,
  },
];
const legalLinks = [
  { name: "Privacy Policy", path: "/privacy-policy" },
  { name: "Terms & Conditions", path: "/terms-and-conditions" },
];



/* =======================
   Footer Component
======================= */

const PublicFooter = () => {
  return (
    <footer className="relative bg-[#161616] text-white">
      {/* Background Blur */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 py-12">
          {/* Logo + About */}
          <div className="space-y-3 lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2 lg:space-x-3 ">
              <img
                src={LOGO}
                alt="LMSBOTS Logo"
                className="h-12 w-auto object-contain transform group-hover:scale-105 transition-transform duration-200"
              />
            </Link>

            <p className="text-slate-300 text-sm">
              Turn prospects into customers with an intelligent lead management
              platform designed to organize, nurture , and accelerate your sales
              pipeline.
            </p>
          </div>

          {/* Company */}
          <div className="space-y-5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
              Company
            </h3>

            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="relative text-slate-300 text-sm hover:text-white transition-all duration-200 hover:translate-x-1 inline-block group"
                  >
                    {link.name}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Products - New Section */}
          <div className="space-y-5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
              Our Products
            </h3>

            <ul className="space-y-4">
              {productLinks.map((product) => {
                const Icon = product.icon;

                return (
                  <li key={product.path}>
                    <Link
                      to={product.path}
                      className="group flex items-start space-x-3 hover:translate-x-1 transition-all duration-200"
                    >
                      <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-purple-500/20 group-hover:to-blue-500/20 transition-all duration-300">
                        <Icon className="w-4 h-4 text-slate-400 group-hover:text-blue-400" />
                      </div>
                      <div>
                        <span className="text-slate-200 text-sm font-medium group-hover:text-white transition-colors duration-200">
                          {product.name}
                        </span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Contact - No underline */}
          <div className="space-y-5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
              Contact
            </h3>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Mail className="w-4 h-4 text-blue-400 mt-1" />
                <div>
                  <p className="text-xs text-slate-400">Email</p>
                  <a
                    href="mailto:info@deetyasoft.com"
                    className="text-slate-200 text-sm hover:text-blue-400 transition-all duration-200"
                  >
                    info@deetyasoft.com
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Phone className="w-4 h-4 text-blue-400 mt-1" />
                <div>
                  <p className="text-xs text-slate-400">Phone</p>
                  <a
                    href="tel:+8855434565"
                    className="text-slate-200 text-sm hover:text-blue-400 transition-all duration-200"
                  >
                    +91 9015350505
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 text-blue-400 mt-1" />
                <div>
                  <p className="text-xs text-slate-400">Address</p>
                  <address className="text-slate-200 text-sm not-italic">
                    FF-06, First Floor, Mahaluxmi Square, Plot No. C-1, Abhay
                    Khand-II, Indirapuram, Ghaziabad, Uttar Pradesh - 201014
                  </address>
                </div>
              </div>
            </div>
          </div>

          {/* Legal + Social */}
          <div className="space-y-5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
              Legal
            </h3>

            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="relative text-slate-300 text-sm hover:text-white transition-all duration-200 hover:translate-x-1 inline-block group"
                  >
                    {link.name}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                </li>
              ))}
            </ul>

            {/* Social Icons */}
           
          </div>
        </div>

        {/* Bottom Bar */}

        <div className="py-6 border-t border-slate-700/50 text-right">
          <p className="text-slate-400 text-sm flex items-center justify-start">
            © {new Date().getFullYear()} LMSBots. All rights reserved.
            <span className="mx-2 w-1 h-1 bg-slate-600 rounded-full"></span>
            <a
              href="https://www.deetyasoft.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-500  transition-colors"
            >
              by DeetyaSoft.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
